'use client'
import React, { useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'

import { PrismicRichText } from '@prismicio/react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import '@/styles/vendors/reactTabs'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'
import { cn } from '@/utils/twMerge'

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

type IndicatorStyle = {
  left: number
  top: number
  width: number
  height: number
}

const FindSolutionComponent = ({ data }: { data: DataObject }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const tabListContainerRef = useRef<HTMLDivElement>(null)
  const [indicator, setIndicator] = useState<IndicatorStyle>({ left: 0, top: 0, width: 0, height: 0 })
  const [indicatorReady, setIndicatorReady] = useState(false)
  const [indicatorAnimated, setIndicatorAnimated] = useState(false)

  useLayoutEffect(() => {
    const container = tabListContainerRef.current
    if (!container) return

    const updateIndicator = () => {
      const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]')
      const activeTab = tabs[tabIndex]
      if (!activeTab) return

      const containerRect = container.getBoundingClientRect()
      const tabRect = activeTab.getBoundingClientRect()

      setIndicator({
        left: tabRect.left - containerRect.left,
        top: tabRect.top - containerRect.top,
        width: tabRect.width,
        height: tabRect.height,
      })
      setIndicatorReady(true)
    }

    updateIndicator()

    // Enable transitions only after the first paint so load doesn't slide from 0.
    const enableMotion = window.requestAnimationFrame(() => {
      setIndicatorAnimated(true)
    })

    const resizeObserver = new ResizeObserver(updateIndicator)
    resizeObserver.observe(container)
    window.addEventListener('resize', updateIndicator)

    return () => {
      window.cancelAnimationFrame(enableMotion)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateIndicator)
    }
  }, [tabIndex, data.items])

  return (
    <section className="pb-[80px] lg:pb-[140px]">
      <div className="container">
        <div className="relative z-[1] overflow-hidden rounded-md border border-primary-ink-6 px-4 py-8 shadow-lg backdrop-blur-25 lg:px-[72px] lg:py-16">
          <div className="absolute right-0 top-0 z-[-1]">
            <Image
              src="/images/find-solution-bg.svg"
              alt="find-solution-bg"
              width={0}
              height={0}
              sizes="100vw"
              className="h-[550px] w-full lg:h-auto"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>

          <PrismicRichText field={data.primary.title} />

          <div className="mt-14">
            <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
              <div ref={tabListContainerRef} className="relative inline-flex">
                <TabList className="relative z-[1] m-0 flex items-center border-b-0">
                  {data.items.map((item: ItemObject, index: number) => {
                    const key = `i-${index}`
                    return (
                      <Tab
                        key={key}
                        className="relative cursor-pointer border-0 bg-transparent px-4 py-2 outline-none focus:outline-none aria-selected:border-0 aria-selected:bg-transparent">
                        {item.tab}
                      </Tab>
                    )
                  })}
                </TabList>

                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute z-0 rounded-[5px] bg-primary-green-20',
                    indicatorReady ? 'opacity-100' : 'opacity-0',
                    indicatorAnimated &&
                      'motion-safe:transition-[left,top,width,height,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.25,0.1,0.25,1)]',
                  )}
                  style={{
                    left: indicator.left,
                    top: indicator.top,
                    width: indicator.width,
                    height: indicator.height,
                  }}
                />
              </div>

              {data.items.map((item: ItemObject, index: number) => {
                const key = `i-${index}`
                const isActive = tabIndex === index

                return (
                  <TabPanel key={key}>
                    {isActive ? (
                      <div key={tabIndex} className="mt-10 grid grid-cols-2 gap-10 motion-safe:animate-fadeIn">
                        <div className="col-span-2 lg:col-span-1">
                          <PrismicRichText field={item.title} />
                          <div className="[&>p]:my-8 [&>ul>li]:relative [&>ul>li]:pl-8 [&>ul>li]:leading-8 [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-1 [&>ul>li]:before:h-5 [&>ul>li]:before:w-5 [&>ul>li]:before:bg-[url('/images/check.svg')] [&>ul>li]:before:content-['']">
                            <PrismicRichText field={item.description} />
                          </div>

                          <PrismicNextLink
                            field={item.btn_link}
                            className="btn btn-primary btn-default btn-interaction mt-8">
                            {item.btn_label}
                            <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                          </PrismicNextLink>
                        </div>
                        <div className="col-span-2 lg:col-span-1">
                          <PrismicNextImage field={item.logo} fallbackAlt="" />
                          <div className="[&>h4]:mb-11 [&>h4]:mt-8">
                            <PrismicRichText field={item.client_quote} />
                          </div>

                          <div className="flex items-center gap-2">
                            <PrismicNextImage field={item.avatar} fallbackAlt="" />
                            <div className="flex flex-col">
                              <h4 className="!m-0">{item.client_name}</h4>
                              <span className="text-sm font-normal">{item.client_designation}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </TabPanel>
                )
              })}
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FindSolutionComponent
